import { useMemo } from 'react';

export const useStats = (config) => {
  const stats = useMemo(() => {
    if (!config || !config.placements) {
      return { 
        total: 0, 
        enabled: 0, 
        contexts: 0, 
        bidders: 0,
        placementsByType: {},
        bidderDistribution: {},
        contextDistribution: {},
        deviceCoverage: { desktop: 0, mobile: 0 },
        averageBiddersPerPlacement: 0
      };
    }
    
    let enabled = 0;
    let contexts = 0;
    const bidders = new Set();
    const placementsByType = {};
    const bidderDistribution = {};
    const contextDistribution = {};
    let desktopPlacements = 0;
    let mobilePlacements = 0;
    let totalBidderCount = 0;
    
    Object.entries(config.placements).forEach(([placementName, placement]) => {
      // Count placement types
      const type = getPlacementType(placementName);
      placementsByType[type] = (placementsByType[type] || 0) + 1;
      
      // Add null check for placement.contexts
      if (!placement || !placement.contexts || typeof placement.contexts !== 'object') {
        console.warn(`[useStats] Skipping placement ${placementName} - invalid contexts:`, placement?.contexts);
        return;
      }
      
      Object.entries(placement.contexts).forEach(([contextName, context]) => {
        // Add null check for context
        if (!context || typeof context !== 'object') {
          console.warn(`[useStats] Skipping context ${contextName} in placement ${placementName} - invalid context:`, context);
          return;
        }
        
        contexts++;
        
        // Count enabled contexts
        if (context.enabled) {
          enabled++;
        }
        
        // Count contexts distribution
        contextDistribution[contextName] = (contextDistribution[contextName] || 0) + 1;
        
        // Count device coverage
        if (context.sizes?.desktop?.length > 0) {
          desktopPlacements++;
        }
        if (context.sizes?.mobile?.length > 0) {
          mobilePlacements++;
        }
        
        // Count bidders
        if (context.bidderParams && typeof context.bidderParams === 'object') {
          const placementBidders = Object.keys(context.bidderParams);
          totalBidderCount += placementBidders.length;
          
          placementBidders.forEach(bidder => {
            bidders.add(bidder);
            bidderDistribution[bidder] = (bidderDistribution[bidder] || 0) + 1;
          });
        }
      });
    });
    
    const totalPlacements = Object.keys(config.placements).length;
    
    return {
      total: totalPlacements,
      enabled,
      contexts,
      bidders: bidders.size,
      placementsByType,
      bidderDistribution,
      contextDistribution,
      deviceCoverage: {
        desktop: desktopPlacements,
        mobile: mobilePlacements
      },
      averageBiddersPerPlacement: totalPlacements > 0 ? (totalBidderCount / contexts).toFixed(1) : 0,
      fillRate: contexts > 0 ? ((enabled / contexts) * 100).toFixed(1) : 0
    };
  }, [config]);

  return stats;
};

// Helper function to determine placement type from name
const getPlacementType = (placementName) => {
  const name = placementName.toLowerCase();
  
  if (name.includes('billboard') || name.includes('leaderboard')) {
    return 'billboard';
  }
  if (name.includes('sidebar') || name.includes('side')) {
    return 'sidebar';
  }
  if (name.includes('inarticle') || name.includes('in-article') || name.includes('content')) {
    return 'inarticle';
  }
  if (name.includes('sticky') || name.includes('floating')) {
    return 'sticky';
  }
  if (name.includes('header')) {
    return 'header';
  }
  if (name.includes('footer')) {
    return 'footer';
  }
  if (name.includes('video')) {
    return 'video';
  }
  
  return 'other';
};

// Hook for performance metrics (would connect to analytics API)
export const usePerformanceStats = (siteId, dateRange = '7d') => {
  // TODO: Implement real analytics API integration
  return {
    fillRate: 0,
    averageCPM: 0,
    totalImpressions: 0,
    totalRevenue: 0,
    topBidders: [],
    dailyStats: []
  };
};

// Hook for real-time monitoring
export const useRealtimeStats = (siteId) => {
  // TODO: Implement WebSocket or Server-Sent Events for real-time data
  return {
    activeUsers: 0,
    currentImpressions: 0,
    avgLoadTime: 0,
    errorRate: 0,
    lastUpdate: new Date().toISOString()
  };
};